import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

// Creator sidebar rail (application/creator_rail/_sidebar.html.haml).
//   - Collapse/expand, remembered in localStorage like the current rail.
//   - Popovers (More, Customize, Help, Account) sit beside the button that
//     opened them; Escape or an outside click closes them.
//   - Customize: drag rows with SortableJS, or focus a row's handle and use
//     ArrowUp/ArrowDown. Done PATCHes {order, pinned} and reloads, so the
//     server re-filters against what the tenant enables.
const STORAGE_KEY = "creatorRailCollapsed";

export default class extends Controller {
  static targets = [
    "popover",
    "sortable",
    "status",
    "toggle",
    "search",
    "rail",
  ];
  static values = { url: String };

  connect() {
    if (localStorage.getItem(STORAGE_KEY) === "true") this.setCollapsed(true);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
    if (this.hasSortableTarget) {
      this.sortable = Sortable.create(this.sortableTarget, {
        handle: ".creator-rail__handle",
        animation: this.reducedMotion ? 0 : 150,
        ghostClass: "is-dragging",
      });
    }
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
    this.sortable?.destroy();
  }

  get reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  get isPhone() {
    return window.innerWidth < 768;
  }

  toggle() {
    this.setCollapsed(!this.element.classList.contains("is-collapsed"));
    localStorage.setItem(
      STORAGE_KEY,
      this.element.classList.contains("is-collapsed")
    );
    if (window.fixCalendar) window.fixCalendar();
  }

  setCollapsed(collapsed) {
    this.element.classList.toggle("is-collapsed", collapsed);
    if (this.hasToggleTarget) {
      const label = collapsed ? "Expand sidebar" : "Collapse sidebar";
      this.toggleTarget.setAttribute("aria-label", label);
      this.toggleTarget.setAttribute("title", label);
      this.toggleTarget.setAttribute("aria-expanded", String(!collapsed));
    }
  }

  expandToSearch() {
    this.setCollapsed(false);
    localStorage.setItem(STORAGE_KEY, "false");
    this.searchTarget.focus();
  }

  togglePopover(event) {
    event.stopPropagation();
    const button = event.currentTarget;
    const name = button.dataset.popover;
    const popover = this.popover(name);
    const wasOpen = !popover.hidden;
    this.closePopovers();
    if (!wasOpen) this.openPopover(popover, button);
  }

  openCustomize(event) {
    event.stopPropagation();
    const anchor = this.element.querySelector("[data-popover='more']");
    this.closePopovers();
    this.openPopover(this.popover("customize"), anchor);
    this.sortableTarget.querySelector(".creator-rail__handle")?.focus();
  }

  popover(name) {
    return this.popoverTargets.find((el) => el.dataset.popover === name);
  }

  openPopover(popover, anchor) {
    popover.hidden = false;
    this.anchor = anchor;
    anchor?.setAttribute("aria-expanded", "true");
    if (this.isPhone) {
      popover.classList.add("is-sheet");
      return;
    }
    popover.classList.remove("is-sheet");
    const rect = anchor.getBoundingClientRect();
    const nav = this.element.getBoundingClientRect();
    const top = Math.min(
      rect.top,
      window.innerHeight - popover.offsetHeight - 12
    );
    popover.style.setProperty("--popover-top", `${Math.max(12, top)}px`);
    popover.style.setProperty("--popover-left", `${nav.right + 8}px`);
  }

  closePopovers() {
    this.popoverTargets.forEach((el) => (el.hidden = true));
    this.element
      .querySelectorAll("[aria-haspopup][aria-expanded='true']")
      .forEach((el) => el.setAttribute("aria-expanded", "false"));
  }

  onDocumentClick(event) {
    if (
      this.popoverTargets.some((el) => !el.hidden && el.contains(event.target))
    )
      return;
    this.closePopovers();
  }

  onKeydown(event) {
    if (event.key !== "Escape" || this.popoverTargets.every((el) => el.hidden))
      return;
    this.closePopovers();
    this.anchor?.focus();
  }

  moveWithKeys(event) {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
    event.preventDefault();
    const row = event.currentTarget.closest("li");
    const sibling =
      event.key === "ArrowUp"
        ? row.previousElementSibling
        : row.nextElementSibling;
    if (!sibling) return;
    if (event.key === "ArrowUp") sibling.before(row);
    else sibling.after(row);
    event.currentTarget.focus();
    const rows = [...this.sortableTarget.children];
    const label = row.querySelector("label").textContent.trim();
    this.statusTarget.textContent = `${label}, position ${
      rows.indexOf(row) + 1
    } of ${rows.length}`;
  }

  resetCustomize() {
    this.save({ order: [], pinned: null });
  }

  saveCustomize() {
    const rows = [...this.sortableTarget.children];
    this.save({
      order: rows.map((row) => row.dataset.key),
      pinned: rows
        .filter((row) => row.querySelector("input").checked)
        .map((row) => row.dataset.key),
    });
  }

  async save(body) {
    const token = document.querySelector("meta[name='csrf-token']")?.content;
    const response = await fetch(this.urlValue, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify(body),
    });
    if (response.ok) window.location.reload();
    else
      this.statusTarget.textContent =
        "Couldn't save your navigation. Try again.";
  }
}
